import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormSelect,
  CFormSwitch,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft } from '@coreui/icons'
import Bootbox from 'bootbox-react'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent'
import { ImportErrorAlert, InfoBox } from 'src/components/common/ImportWizard'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import PlayHQService from 'src/service/PlayHQService'
import { LoadingBlock, errorsOf, tableHeadStyle } from './playhqShared'
import RoundSyncForm, { roundDate } from './RoundSyncForm'

// Where one fixture stands after the last sync.
const FIXTURE_STATUS = {
  synced: { label: 'Synced', color: 'success' },
  partial: { label: 'Synced, players left out', color: 'warning' },
  waiting: { label: 'Waiting for team sheet', color: 'info' },
  needs_attention: { label: 'Needs attention', color: 'warning' },
  error: { label: 'Failed', color: 'danger' },
}

// What the last run did for the whole game.
const RUN_STATUS = {
  done: { label: 'Done', color: 'success' },
  attention: { label: 'Some fixtures need attention', color: 'warning' },
  blocked: { label: 'Not run', color: 'danger' },
  idle: { label: 'Nothing to sync', color: 'secondary' },
  skipped: { label: 'Skipped', color: 'secondary' },
  error: { label: 'Failed', color: 'danger' },
}

// How far a round's fixtures have got, whoever synced them (the cron or Sync now). Completed
// fixtures are out of it: they can no longer be synced.
const roundProgress = (round) => {
  const { fixtures_total: total, fixtures_open: open, fixtures_synced: synced } = round
  if (!total) return { label: 'No fixtures', color: 'secondary' }
  if (!open) return { label: 'All completed', color: 'dark' }
  if (synced === open) return { label: `All synced (${synced}/${open})`, color: 'success' }
  if (synced) return { label: `Synced ${synced} of ${open}`, color: 'warning' }
  return { label: 'Not synced yet', color: 'secondary' }
}

// What the cron is doing with a round. Its days-before decide when the next round joins in.
const roundAutoSync = (round) => {
  if (round.syncing) return 'Keeping it up to date'
  if (round.state === 'next') return `Starts ${roundDate(round.sync_from)}`
  if (round.state === 'past') return 'Finished'
  return 'Not started'
}

const when = (iso) => (iso ? `${moment(iso).format('ddd D MMM, h:mm a')}` : '')

/**
 * PlayHQ Auto Sync. While it is switched on a cron pulls, every 15 minutes, the squad and
 * scorecard of the not-started fixtures (status 0) of the current round and the next one - the
 * next round from its chosen 1-3 days before it starts - from the PlayHQ match each is linked to. It runs only once all of those fixtures are linked; until then the
 * admin imports by hand on the PlayHQ Import page. While it is on it overwrites hand-made squad
 * and scorecard changes, so an admin who wants to work by hand switches it off first.
 */
const PlayHQAutoSync = () => {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const [saving, setSaving] = useState(false)
  const [confirmOn, setConfirmOn] = useState(false)
  const [running, setRunning] = useState(null)
  const [editRound, setEditRound] = useState(null)
  // '' = all current and upcoming fixtures, 'auto' = the ones auto sync works on, or a round id.
  const [fixtureFilter, setFixtureFilter] = useState('')

  useEffect(() => {
    PlayHQService.getAutoSync()
      .then((res) => (res.success ? setData(res.data) : setLoadError(errorsOf(res)[0])))
      .catch(() => setLoadError('This page could not be loaded. Please refresh to try again.'))
  }, [])

  // The switch saves straight away.
  const setAutoSync = async (on) => {
    setConfirmOn(false)
    setSaving(true)
    try {
      const res = await PlayHQService.saveAutoSync({ auto_sync: on })
      if (!res.success) {
        ToastComponent(errorsOf(res)[0], 'error')
        return
      }
      setData(res.data)
      ToastComponent(res.message, 'success')
    } catch (e) {
      ToastComponent('Auto sync could not be switched. Please try again.', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Switching it on means hand-made squad and scorecard changes get overwritten: say so first.
  const onToggle = (on) => (on ? setConfirmOn(true) : setAutoSync(false))

  // Sync now on one round ({ round_id }) or one fixture ({ fixture_id }). `running` is the key of
  // the row being synced, so only that row shows it; the server runs one sync per game at a time.
  const syncNow = async (key, target) => {
    setRunning(key)
    try {
      const res = await PlayHQService.runAutoSync(target)
      if (res.data?.link) setData(res.data)
      ToastComponent(res.message || errorsOf(res)[0], res.success ? 'success' : 'error')
    } catch (e) {
      ToastComponent('Auto sync could not be run. Please try again.', 'error')
    } finally {
      setRunning(null)
    }
  }

  const header = (
    <>
      <button type="button" className="ge-back-link" onClick={() => navigate('/playhq-import')}>
        <CIcon icon={cilArrowLeft} />
        Back to PlayHQ Import
      </button>
      <div className="ge-page-header">
        <h1 className="ge-page-title">PlayHQ Auto Sync</h1>
        <p className="ge-page-subtitle">
          Keep squads and scorecards of this round&apos;s and next round&apos;s fixtures up to date
          from play.cricket.com.au.
        </p>
      </div>
    </>
  )

  if (loadError) {
    return (
      <>
        {header}
        <ImportErrorAlert errors={[loadError]} title="Something went wrong:" />
      </>
    )
  }
  if (!data) return <LoadingBlock text="Loading…" />

  if (!data.link) {
    return (
      <>
        {header}
        <CCard className="border-0 shadow-sm">
          <CCardBody>
            <InfoBox color="light">
              Auto sync works from your PlayHQ link. Link your club or association on the PlayHQ
              Import page first.
            </InfoBox>
            <CButton color="primary" onClick={() => navigate('/playhq-import')}>
              Go to PlayHQ Import
            </CButton>
          </CCardBody>
        </CCard>
      </>
    )
  }

  const fixtures = data.fixtures || []
  const rounds = data.rounds || []
  // Only the fixtures auto sync works on hold it back when they are not linked.
  const unlinked = fixtures.filter((f) => f.auto && !f.linked)
  const roundById = Object.fromEntries(rounds.map((r) => [r.id, r]))
  // Rounds with fixtures in the list, for the filter: [round id, label, fixture count].
  const fixtureRounds = Object.values(
    fixtures.reduce((acc, f) => {
      const entry = acc[f.round_id] || [String(f.round_id), `Round ${f.round ?? '-'}`, 0]
      entry[2] += 1
      return { ...acc, [f.round_id]: entry }
    }, {}),
  )
  const shownFixtures = fixtures.filter((f) =>
    fixtureFilter === 'auto' ? f.auto : fixtureFilter ? String(f.round_id) === fixtureFilter : true,
  )
  const leftOut = data.left_out || []
  const run = data.last_run
  const runStatus = run && (RUN_STATUS[run.status] || RUN_STATUS.idle)
  // Sync now works whether auto sync is on or off, one row at a time.
  const syncAction = (key, target) => ({
    label: running === key ? 'Syncing…' : 'Sync now',
    disabled: running !== null,
    onClick: () => syncNow(key, target),
  })

  return (
    <>
      {header}
      <CRow className="g-3">
        {leftOut.length > 0 && (
          <CCol xs={12}>
            <LeftOutCard
              fixtures={leftOut}
              syncAction={syncAction}
              running={running}
              onLinkPlayers={() => navigate('/playhq-import?tab=players')}
            />
          </CCol>
        )}

        <CCol xs={12}>
          <CCard className="border-0 shadow-sm">
            <CCardBody>
              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="fw-semibold mb-1">Auto sync settings</h5>
                  <div className="small text-medium-emphasis">
                    Linked to {data.link.org_name}. Round times are in {data.timezone} time.
                  </div>
                </div>
                <CFormSwitch
                  size="lg"
                  id="playhq_auto_sync"
                  label={data.auto_sync ? 'On' : 'Off'}
                  checked={data.auto_sync}
                  disabled={saving}
                  onChange={(e) => onToggle(e.target.checked)}
                />
              </div>
              <InfoBox className="mb-0">
                While auto sync is on it checks every 15 minutes. Once a fixture&apos;s team sheet
                is on play.cricket it saves the squad, then keeps the scorecard up to date. If
                play.cricket changes the squad, that fixture&apos;s squad and scorecard are saved
                again from scratch. Only the not-started fixtures of the current round and the next
                round are synced - the next round from the days before it you choose under Rounds -
                and only when all of them are linked to a PlayHQ match. Once you move a fixture to
                In Progress or Completed, auto sync leaves it alone, and marking a fixture complete
                stays with you. While auto sync is on it overwrites changes made by hand, so switch
                it off before editing a squad or scorecard yourself.
              </InfoBox>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12}>
          <CCard className="border-0 shadow-sm">
            <CCardBody>
              <h5 className="fw-semibold mb-1">Last run</h5>
              {run ? (
                <>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <CBadge shape="rounded-pill" color={runStatus.color}>
                      {runStatus.label}
                    </CBadge>
                    <span className="small text-medium-emphasis">{when(run.at)}</span>
                  </div>
                  <div className="small">{run.message}</div>
                </>
              ) : (
                <div className="small text-medium-emphasis">Auto sync has not run yet.</div>
              )}
              <div className="small text-medium-emphasis mt-2">
                To sync straight away, use Sync now on a round or a fixture below - it works whether
                auto sync is on or off.
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12}>
          <CCard className="border-0 shadow-sm">
            <CCardBody>
              <h5 className="fw-semibold mb-1">Rounds</h5>
              <div className="small text-medium-emphasis mb-3">
                Auto sync works on the current round and the next one. Choose how many days before a
                round starts its fixtures join in. Sync now syncs any round straight away, with auto
                sync on or off - every fixture in it that is linked and not completed.
              </div>
              {rounds.length === 0 ? (
                <InfoBox color="light" className="mb-0">
                  This game has no rounds yet. Add rounds on the Rounds page, or import them on the
                  PlayHQ Import Rounds tab.
                </InfoBox>
              ) : (
                <CTable hover responsive className="align-middle mb-0 small">
                  <CTableHead style={tableHeadStyle}>
                    <CTableRow>
                      <CTableHeaderCell>Round</CTableHeaderCell>
                      <CTableHeaderCell>Starts</CTableHeaderCell>
                      <CTableHeaderCell>Ends</CTableHeaderCell>
                      <CTableHeaderCell>Syncs from</CTableHeaderCell>
                      <CTableHeaderCell>Fixtures synced</CTableHeaderCell>
                      <CTableHeaderCell>Auto sync</CTableHeaderCell>
                      <CTableHeaderCell className="text-end">Action</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {rounds.map((r) => {
                      const status =
                        running === `round:${r.id}`
                          ? { label: 'Syncing…', color: 'primary' }
                          : roundProgress(r)
                      return (
                        <CTableRow key={r.id}>
                          <CTableDataCell className="fw-semibold">Round {r.round}</CTableDataCell>
                          <CTableDataCell className="text-nowrap">
                            {roundDate(r.start_date)}
                          </CTableDataCell>
                          <CTableDataCell className="text-nowrap">
                            {roundDate(r.end_date)}
                          </CTableDataCell>
                          <CTableDataCell className="text-nowrap">
                            {roundDate(r.sync_from)}
                            <div className="text-medium-emphasis">
                              {r.days_before} day{r.days_before === 1 ? '' : 's'} before
                            </div>
                          </CTableDataCell>
                          <CTableDataCell>
                            <CBadge shape="rounded-pill" color={status.color}>
                              {status.label}
                            </CBadge>
                          </CTableDataCell>
                          <CTableDataCell
                            className={`text-nowrap ${
                              r.syncing ? 'text-success fw-semibold' : 'text-medium-emphasis'
                            }`}
                          >
                            {roundAutoSync(r)}
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <RowActionsMenu
                              actions={[
                                syncAction(`round:${r.id}`, { round_id: r.id }),
                                { label: 'Edit', onClick: () => setEditRound(r) },
                              ]}
                            />
                          </CTableDataCell>
                        </CTableRow>
                      )
                    })}
                  </CTableBody>
                </CTable>
              )}
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12}>
          <CCard className="border-0 shadow-sm">
            <CCardBody>
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                <div>
                  <h5 className="fw-semibold mb-1">Current and upcoming fixtures</h5>
                  <div className="small text-medium-emphasis">
                    Every fixture from the current round on that is not completed. Use Sync now on
                    any of them; the ones marked Auto syncing are also kept up to date by auto sync.
                  </div>
                </div>
                <CFormSelect
                  style={{ maxWidth: 260 }}
                  value={fixtureFilter}
                  onChange={(e) => setFixtureFilter(e.target.value)}
                  aria-label="Show fixtures"
                >
                  <option value="">All ({fixtures.length})</option>
                  <option value="auto">
                    Auto syncing ({fixtures.filter((f) => f.auto).length})
                  </option>
                  {fixtureRounds.map(([id, label, count]) => (
                    <option key={id} value={id}>
                      {label} ({count})
                    </option>
                  ))}
                </CFormSelect>
              </div>
              {unlinked.length > 0 && (
                <InfoBox color="warning">
                  {unlinked.length} fixture{unlinked.length === 1 ? '' : 's'} that auto sync is
                  working on {unlinked.length === 1 ? 'is' : 'are'} not linked to a PlayHQ match, so
                  auto sync will not run. Link {unlinked.length === 1 ? 'it' : 'them'} on the PlayHQ
                  Import Fixtures tab, or keep importing by hand.
                </InfoBox>
              )}
              {shownFixtures.length === 0 ? (
                <InfoBox color="light" className="mb-0">
                  {fixtures.length === 0
                    ? 'There are no fixtures left to sync from the current round on - they are all completed, or no rounds have fixtures yet.'
                    : 'Auto sync is not working on any fixture right now. It picks up the current round, and the next round from its days before. Use Sync now on any fixture under All.'}
                </InfoBox>
              ) : (
                <CTable hover responsive className="align-middle mb-0 small">
                  <CTableHead style={tableHeadStyle}>
                    <CTableRow>
                      <CTableHeaderCell>Round</CTableHeaderCell>
                      <CTableHeaderCell>Fixture</CTableHeaderCell>
                      <CTableHeaderCell>PlayHQ match</CTableHeaderCell>
                      <CTableHeaderCell>Auto sync</CTableHeaderCell>
                      <CTableHeaderCell>Sync</CTableHeaderCell>
                      <CTableHeaderCell>Details</CTableHeaderCell>
                      <CTableHeaderCell>Last checked</CTableHeaderCell>
                      <CTableHeaderCell className="text-end">Action</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {shownFixtures.map((f) => (
                      <FixtureRow
                        key={f.id}
                        fixture={f}
                        round={roundById[f.round_id]}
                        syncing={running === `fixture:${f.id}`}
                        action={syncAction(`fixture:${f.id}`, { fixture_id: f.id })}
                      />
                    ))}
                  </CTableBody>
                </CTable>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <Bootbox
        show={confirmOn}
        type={'confirm'}
        message="Turn auto sync on? It will save squads and scorecards from play.cricket, replacing any changes made by hand to the not-started fixtures of the current and next round."
        onSuccess={() => setAutoSync(true)}
        onCancel={() => setConfirmOn(false)}
        onClose={() => setConfirmOn(false)}
      />
      <RoundSyncForm round={editRound} onClose={() => setEditRound(null)} onSaved={setData} />
    </>
  )
}

/**
 * Fixtures whose last sync left PlayHQ players out because they are not linked to one of ours, in
 * any round. Linking them on the Players tab and syncing again rebuilds the squad and scorecard
 * with them in.
 */
const LeftOutCard = ({ fixtures, syncAction, running, onLinkPlayers }) => {
  const players = fixtures.reduce((sum, f) => sum + f.players.length, 0)
  return (
    <CCard className="border-0 shadow-sm">
      <CCardBody>
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
          <div>
            <h5 className="fw-semibold mb-1">Players left out</h5>
            <div className="small text-medium-emphasis">
              {players} player{players === 1 ? '' : 's'} in {fixtures.length} fixture
              {fixtures.length === 1 ? ' was' : 's were'} not synced because{' '}
              {players === 1 ? 'it is' : 'they are'} not linked to one of your players. Their runs,
              wickets and catches are missing from the squad and scorecard.
            </div>
          </div>
          <CButton color="warning" onClick={onLinkPlayers}>
            Link players
          </CButton>
        </div>
        <InfoBox color="warning">
          Link these players on the PlayHQ Import Players tab, then use Sync now on the fixture. The
          squad and scorecard are saved again with them in. Auto sync does the same on its next run
          for fixtures in the rounds it is syncing.
        </InfoBox>
        <CTable hover responsive className="align-middle mb-0 small">
          <CTableHead style={tableHeadStyle}>
            <CTableRow>
              <CTableHeaderCell>Round</CTableHeaderCell>
              <CTableHeaderCell>Fixture</CTableHeaderCell>
              <CTableHeaderCell>Players not synced (PlayHQ names)</CTableHeaderCell>
              <CTableHeaderCell>Last synced</CTableHeaderCell>
              <CTableHeaderCell className="text-end">Action</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {fixtures.map((f) => (
              <CTableRow key={f.id}>
                <CTableDataCell className="text-nowrap">
                  {f.round ? `Round ${f.round}` : '-'}
                </CTableDataCell>
                <CTableDataCell className="fw-semibold">{f.label}</CTableDataCell>
                <CTableDataCell>
                  <div className="d-flex flex-wrap gap-1">
                    {f.players.map((name, i) => (
                      <CBadge key={`${i}-${name}`} shape="rounded-pill" color="warning">
                        {name}
                      </CBadge>
                    ))}
                  </div>
                </CTableDataCell>
                <CTableDataCell className="text-nowrap text-medium-emphasis">
                  {running === `fixture:${f.id}` ? 'Syncing…' : when(f.synced_at) || '-'}
                </CTableDataCell>
                <CTableDataCell className="text-end text-nowrap">
                  <RowActionsMenu actions={[syncAction(`fixture:${f.id}`, { fixture_id: f.id })]} />
                </CTableDataCell>
              </CTableRow>
            ))}
          </CTableBody>
        </CTable>
      </CCardBody>
    </CCard>
  )
}

// Whether the cron works on a fixture, and if not, when or why not. Sync now works either way.
const fixtureAutoSync = (fixture, round) => {
  if (fixture.auto) return null
  if (fixture.status === 1) return 'In progress - Sync now only'
  if (round?.state === 'next' || round?.state === 'upcoming') {
    return `From ${roundDate(round.sync_from)}`
  }
  return 'Sync now only'
}

const FixtureRow = ({ fixture, round, syncing, action }) => {
  const sync = fixture.sync
  const status = syncing
    ? { label: 'Syncing…', color: 'primary' }
    : sync && FIXTURE_STATUS[sync.status]
  const autoNote = fixtureAutoSync(fixture, round)
  return (
    <CTableRow>
      <CTableDataCell className="text-nowrap">
        {fixture.round ? `Round ${fixture.round}` : '-'}
      </CTableDataCell>
      <CTableDataCell className="fw-semibold">{fixture.label}</CTableDataCell>
      <CTableDataCell>
        <CBadge shape="rounded-pill" color={fixture.linked ? 'success' : 'danger'}>
          {fixture.linked ? 'Linked' : 'Not linked'}
        </CBadge>
      </CTableDataCell>
      <CTableDataCell className="text-nowrap">
        {autoNote === null ? (
          <CBadge shape="rounded-pill" color="success">
            Auto syncing
          </CBadge>
        ) : (
          <span className="text-medium-emphasis">{autoNote}</span>
        )}
      </CTableDataCell>
      <CTableDataCell>
        <CBadge shape="rounded-pill" color={status ? status.color : 'secondary'}>
          {status ? status.label : 'Not synced yet'}
        </CBadge>
      </CTableDataCell>
      <CTableDataCell>
        {sync?.note || <span className="text-medium-emphasis">-</span>}
        {sync?.unlinked_players?.length > 0 && (
          <div className="text-medium-emphasis mt-1">
            Not linked: {sync.unlinked_players.join(', ')}. Link them on the PlayHQ Import Players
            tab.
          </div>
        )}
      </CTableDataCell>
      <CTableDataCell className="text-nowrap text-medium-emphasis">
        {when(sync?.synced_at) || '-'}
      </CTableDataCell>
      <CTableDataCell className="text-end text-nowrap">
        <RowActionsMenu actions={[action]} />
      </CTableDataCell>
    </CTableRow>
  )
}

export default PlayHQAutoSync
